// Reexport auth functions from firebaseService
import { 
  auth, 
  signInWithGoogle, 
  logInWithEmailAndPassword, 
  registerWithEmailAndPassword, 
  sendPasswordReset, 
  logout 
} from '../services/firebaseService';

// Export auth functions
export { 
  signInWithGoogle, 
  logInWithEmailAndPassword, 
  registerWithEmailAndPassword, 
  sendPasswordReset, 
  logout 
};

// Store the backend token in localStorage
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('roamZapToken', token);
  } else {
    localStorage.removeItem('roamZapToken');
  }
};

// Get the stored token
export const getAuthToken = () => {
  const token = localStorage.getItem('roamZapToken');
  return token || null;
};

// Function to send user data to backend and get token
export const authenticateWithBackend = async (user) => {
  if (!user) return null;
  
  try {
    console.log('Authenticating with backend, user:', user);
    console.log('User photoURL before token:', user.photoURL);
    
    // Get the ID token from Firebase
    const idToken = await user.getIdToken();
    console.log('Firebase ID token obtained');
    
    // Ensure we have the latest user data
    if (user.reload) {
      await user.reload();
      console.log('User reloaded, updated photoURL:', user.photoURL);
    }
    
    const requestBody = {
      firebaseToken: idToken,
      user: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email.split('@')[0],
        photoURL: user.photoURL || '',
        emailVerified: user.emailVerified
      }
    };
    
    console.log('Backend auth request body:', JSON.stringify(requestBody));
    console.log('API URL:', process.env.REACT_APP_API_URL);
    
    const url = `${process.env.REACT_APP_API_URL}/auth/firebase-auth`;
    console.log('Full auth URL:', url);
    
    // Send the token to our backend with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
        credentials: 'include' // Include cookies
      });
      
      clearTimeout(timeoutId);
      
      console.log('Backend response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend auth error response:', errorData);
        throw new Error(errorData.message || 'Authentication with backend failed');
      }
      
      const data = await response.json();
      console.log('Backend auth success:', data);
      
      // Store the backend token using the helper
      const token = data.data.token;
      if (token) {
        setAuthToken(token);
        console.log('Token successfully stored');
        
        // Store the login timestamp for reference
        localStorage.setItem('roamZapLoginTime', Date.now().toString());
        
        // Get additional user info from data if available
        if (data.data.user) {
          localStorage.setItem('roamZapUserInfo', JSON.stringify(data.data.user));
        }
      } else {
        console.error('No token found in response data');
      }
      
      return data;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      console.error('Fetch error details:', fetchError);
      
      // For debugging - try a simple fetch to root endpoint
      try {
        console.log('Attempting to fetch root endpoint for debugging...');
        const testResponse = await fetch(process.env.REACT_APP_API_URL);
        console.log('Root endpoint response:', testResponse.status);
      } catch (testError) {
        console.error('Even test request failed:', testError);
      }
      
      throw fetchError;
    }
  } catch (error) {
    console.error('Backend authentication error:', error);
    return null;
  }
};

// Function to get user profile from backend
export const fetchUserProfile = async () => {
  const token = getAuthToken();
  
  if (!token) {
    console.log('No token found, cannot fetch profile');
    return null;
  }
  
  try {
    console.log('Fetching user profile with token:', token.substring(0, 10) + '...');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/users/profile`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('Profile response status:', response.status);
    
    if (!response.ok) {
      if (response.status === 401) {
        // Token expired or invalid
        setAuthToken(null);
        console.log('Token expired or invalid, removed from storage');
        return null;
      }
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch user profile');
    }
    
    const data = await response.json();
    console.log('Profile data retrieved:', data);
    return data.data;
  } catch (error) {
    console.error('Fetch profile error:', error);
    return null;
  }
};

// Function to update user profile in backend
export const updateUserProfile = async (profileData) => {
  const token = localStorage.getItem('roamZapToken');
  
  if (!token) throw new Error('Not authenticated');
  
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/users/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update user profile');
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Update profile error:', error);
    throw error;
  }
};

// Function to logout from backend
export const logoutFromBackend = async () => {
  const token = getAuthToken();
  
  if (!token) return;
  
  try {
    console.log('Logging out from backend...');
    
    // We'll submit an empty body since the backend will use the token
    // to find the current session
    const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({})
    });
    
    console.log('Logout response status:', response.status);
    
    // Always remove the token, even if request fails
    setAuthToken(null);
    
    if (response.ok) {
      console.log('Successfully logged out from backend');
    } else {
      const errorData = await response.json();
      console.error('Logout error response:', errorData);
    }
  } catch (error) {
    console.error('Logout error:', error);
    // Still remove the token if there's an error
    setAuthToken(null);
  }
}; 